import { useEffect, useState, type RefObject } from 'react';

/**
 * Detects whether a header element has scrolled past the topbar.
 * Returns `true` when the bottom edge of `headerRef` is above the topbar,
 * meaning the header is no longer visible and a melt bar should appear.
 *
 * Mirrors the scroll-detection logic originally in EventDetail.tsx.
 */
export function useHeaderMelt(headerRef: RefObject<HTMLElement | null>): boolean {
  const [melted, setMelted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      setMelted(false);
      return;
    }

    const getTopbarHeight = (): number => {
      const v =
        typeof document !== 'undefined' && document.documentElement
          ? getComputedStyle(document.documentElement)
              .getPropertyValue('--topbar-viewport-height')
              .trim()
          : '';
      const parsed = v ? parseInt(v.replace(/px$/, ''), 10) : NaN;
      return Number.isFinite(parsed) ? parsed : 72;
    };

    let observedHeader: HTMLElement | null = null;
    let ro: ResizeObserver | null = null;
    let active = true;

    const observeCurrentHeader = () => {
      const el = headerRef.current;
      if (!ro || !el || el === observedHeader) return;
      if (observedHeader) ro.unobserve?.(observedHeader);
      ro.observe(el);
      observedHeader = el;
    };

    const check = () => {
      if (!active || typeof window === 'undefined' || typeof document === 'undefined') return;
      observeCurrentHeader();
      const el = headerRef.current;
      if (!el) {
        setMelted((prev) => (prev ? false : prev));
        return;
      }
      const rect = el.getBoundingClientRect();
      const topbarHeight = getTopbarHeight();
      const buffer = 8;
      const scrolledPast = rect.bottom < topbarHeight + buffer;
      setMelted((prev) => (prev !== scrolledPast ? scrolledPast : prev));
    };

    ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    check();
    const onScroll = () => check();
    const onResize = () => check();

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onResize);
    const appShell = document.querySelector('.app-shell');
    if (appShell) appShell.addEventListener('scroll', onScroll, { passive: true });

    const mutationObserver = typeof MutationObserver !== 'undefined' && typeof document !== 'undefined'
      ? new MutationObserver(check)
      : null;
    if (mutationObserver && document.body) {
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      active = false;
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      if (appShell) appShell.removeEventListener('scroll', onScroll);
      if (observedHeader) ro?.unobserve?.(observedHeader);
      ro?.disconnect?.();
      mutationObserver?.disconnect();
    };
  }, [headerRef]);

  return melted;
}

export default useHeaderMelt;
